import React, { useState } from "react";
import { Container, Card, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_GROUP_VERSION_MUTATION } from "../../../../graphql/mutations";
import GroupVersionDetails from "./sections/GroupVersionDetails";

const AddGroupVersionPage = ({ env, handleEnvChange, currentTenant, tenantName, isLoading, isError, groups }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    groupId: "",
    ogmpYear: "",
    ogmpLevel: "",
    plannedSiteSurveys: "",
    uncertaintyFraction: "",
    productionCh4: "",
    productionCh4Unit: "",
    typeOfEquity: "",
    equityFraction: "",
    comments: ""
  });
  const [formErrors, setFormErrors] = useState({});

  const [createGroupVersion, { loading: mutationLoading }] = useMutation(CREATE_GROUP_VERSION_MUTATION);

  const isTenantSelected = tenantName !== null && tenantName !== "undefined" && tenantName !== "null" && tenantName !== "";

  const tenantHasProjectGroups = isTenantSelected && groups?.length >= 1;

  const enableGroupVersionButton = isTenantSelected && tenantHasProjectGroups;

  const handleFormChange = (field, value, hasError = false) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value
    }));

    // always start with a fresh copy of current errors
    const updatedErrors = { ...formErrors };

    // check for duplicate OGMP year
    if (field === "ogmpYear" && value && formData.groupId) {
      const selectedGroup = groups.find((group) => group.id === formData.groupId);
      if (selectedGroup && selectedGroup.ogmpVersions && selectedGroup.ogmpVersions.includes(parseInt(value, 10))) {
        updatedErrors[field] = "This group already has a version for this year";
      } else {
        delete updatedErrors[field];
      }
    } else if (hasError) {
      updatedErrors[field] = "This group already has a version for this year";
    } else {
      if (value) {
        delete updatedErrors[field];
      }
    }

    setFormErrors(updatedErrors);
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ["ogmpYear", "ogmpLevel", "groupId", "productionCh4", "productionCh4Unit", "uncertaintyFraction"];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
      }
    });

    // check for duplicate OGMP year
    if (formData.groupId && formData.ogmpYear) {
      const selectedGroup = groups.find((group) => group.id === formData.groupId);
      if (selectedGroup && selectedGroup.ogmpVersions && selectedGroup.ogmpVersions.includes(parseInt(formData.ogmpYear, 10))) {
        errors.ogmpYear = "This group already has a version for this year";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    const ogmpYear = formData.ogmpYear ? parseInt(formData.ogmpYear, 10) : null;
    const plannedSiteSurveys = formData.plannedSiteSurveys ? parseInt(formData.plannedSiteSurveys, 10) : null;
    const productionCh4 = formData.productionCh4 ? parseFloat(formData.productionCh4) : null;
    const productionCh4Unit = formData.productionCh4 ? formData.productionCh4Unit : null;
    const equityFraction = formData.equityFraction ? parseFloat(formData.equityFraction) : null;
    const uncertaintyFraction = formData.uncertaintyFraction ? parseFloat(formData.uncertaintyFraction) : null;

    try {
      const { data } = await createGroupVersion({
        variables: {
          groupVersionInput: {
            groupId: formData?.groupId,
            ogmpYear: ogmpYear,
            ogmpLevel: formData?.ogmpLevel,
            plannedSiteSurveys: plannedSiteSurveys,
            uncertaintyFraction: uncertaintyFraction,
            productionCh4: productionCh4,
            productionCh4Unit: productionCh4Unit,
            typeOfEquity: formData?.typeOfEquity,
            equityFraction: equityFraction,
            comments: formData?.comments
          }
        }
      });

      if (data?.createGroupVersion) {
        toast.success(`Group version created successfully`);
        navigate("/group-versions");
        handleEnvChange(env, currentTenant, tenantName);
      }
    } catch (error) {
      toast.error(`Failed to create group version: ${error.message}`);
    }
  };

  return (
    <Container className="mt-1">
      <h4 className="mb-4">Add New Group Version</h4>
      <Card className="border mb-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <GroupVersionDetails
              isLoading={isLoading}
              isError={isError}
              onFormChange={handleFormChange}
              formData={formData}
              formErrors={formErrors}
              groups={groups}
            />
            {enableGroupVersionButton ? (
              <div className="d-grid">
                <Button
                  variant="primary"
                  type="submit"
                  className="mt-3 mb-3 mx-auto"
                  style={{ width: "25%" }}
                  disabled={mutationLoading || Object.keys(formErrors).length > 0}
                >
                  {mutationLoading ? "Creating Group Version..." : "Create Group Version"}
                </Button>
              </div>
            ) : (
              <div className="d-grid">
                <Button variant="primary" type="submit" className="mt-3 mb-3 mx-auto" style={{ width: "25%" }} disabled>
                  Add a group to tenant first
                </Button>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddGroupVersionPage;
